<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import DatePicker from '../../Shared/DatePicker.vue'

const { t } = useI18n()
const { umsokn, onLoaded } = useUmsokn()

const props = defineProps<{
  disable?: boolean
}>()

// validation rules
const rules = {
  umsokn: {
    healthDoctors: {
      $each: {
        name: { required }
      }
    },
    healthorganizations: {
      $each: {
        name: { required },
        from: { required },
        to: { required }
      }
    }
  }
}

const v$ = useVuelidate(rules, { umsokn })

// initialize collections when umsokn is loaded
onLoaded(() => {
  if (umsokn.value) {
    if (!umsokn.value.healthDoctors || umsokn.value.healthDoctors.length === 0) {
      umsokn.value.healthDoctors = [{ name: '' }]
    }
    if (!umsokn.value.healthorganizations || umsokn.value.healthorganizations.length === 0) {
      umsokn.value.healthorganizations = [{ name: '', from: null, to: null }]
    }
  }
})

function addHealthDoctor() {
  umsokn.value?.healthDoctors.push({ name: '' })
}
function removeHealthDoctor(index: number) {
  umsokn.value?.healthDoctors.splice(index, 1)
}

function addHealthOrg() {
  umsokn.value?.healthorganizations.push({ name: '', from: null, to: null })
}
function removeHealthOrg(index: number) {
  umsokn.value?.healthorganizations.splice(index, 1)
}

defineExpose({
  validate() {
    v$.value.$touch()
    return !v$.value.$invalid
  }
})
</script>

<template>
  <div v-if="umsokn">
    <h2 class="form__header">{{ t('ororku_umsokn_step9_header') }}</h2>

    <!-- Doctors -->
    <p class="mb-4">{{ t('ororku_umsokn_step9_healthDoctors') }}</p>
    <transition-group name="list">
      <div v-for="(item, index) in umsokn.healthDoctors" :key="'doctor_' + index">
        <div v-if="index > 0" class="forms__divider__container">
          <div class="forms__divider"></div>
          <button v-if="!props.disable" class="forms__warning__list__wrapper" @click="removeHealthDoctor(index)">
            <img class="forms__warning__icon" src="@/assets/icons/Close.svg" />
          </button>
        </div>
        <v-text-field
          v-model="item.name"
          variant="outlined"
          density="comfortable"
          :readonly="props.disable"
          :label="t('ororku_umsokn_step9_healthorganizations_doctor_name')"
          :error="!!v$.umsokn.healthDoctors?.$each?.[index]?.name?.$errors?.length"
          :error-messages="v$.umsokn.healthDoctors?.$each?.[index]?.name?.$errors?.map(() =>
            t('ororku_umsokn_step9_healthorganizations_doctor_name_error')
          ) || []"
        />
      </div>
    </transition-group>
    <div v-if="!props.disable" class="m-t-40">
       <v-btn
          color="primary"
          variant="outlined"
          prepend-icon="mdi-plus"
          class="mt-4"
          @click="addHealthDoctor"
        >
          {{ $t('ororku_umsokn_step3_add') }}
        </v-btn>
    </div>

    <!-- Health organizations -->
    <p class="mt-10">{{ t('ororku_umsokn_step9_header2') }}</p>
    <transition-group name="list">
      <div v-for="(item, index) in umsokn.healthorganizations" :key="'org_' + index">
        <div v-if="index > 0" class="forms__divider__container">
          <div class="forms__divider"></div>
          <button v-if="!props.disable" class="forms__warning__list__wrapper" @click="removeHealthOrg(index)">
            <img class="forms__warning__icon" src="@/assets/icons/Close.svg" />
          </button>
        </div>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="item.name"
              variant="outlined"
              density="comfortable"
              :readonly="props.disable"
              :label="t('ororku_umsokn_step9_healthorganizations_name')"
              :error="!!v$.umsokn.healthorganizations?.$each?.[index]?.name?.$errors?.length"
              :error-messages="v$.umsokn.healthorganizations?.$each?.[index]?.name?.$errors?.map(() =>
                t('ororku_umsokn_step9_healthorganizations_name_error')
              ) || []"
            />
          </v-col>
          <v-col cols="12" md="3">
            <DatePicker
              v-model="item.from"
              :disabled="props.disable"
              :label="t('ororku_umsokn_step9_healthorganizations_from')"
              :error="!!v$.umsokn.healthorganizations?.$each?.[index]?.from?.$errors?.length"
              :error-messages="v$.umsokn.healthorganizations?.$each?.[index]?.from?.$errors?.map(() =>
                t('ororku_umsokn_step9_healthorganizations_from_error')
              ) || []"
            />
          </v-col>
          <v-col cols="12" md="3">
            <DatePicker
              v-model="item.to"
              :disabled="props.disable"
              :label="t('ororku_umsokn_step9_healthorganizations_to')"
              :error="!!v$.umsokn.healthorganizations?.$each?.[index]?.to?.$errors?.length"
              :error-messages="v$.umsokn.healthorganizations?.$each?.[index]?.to?.$errors?.map(() =>
                t('ororku_umsokn_step9_healthorganizations_to_error')
              ) || []"
            />
          </v-col>
        </v-row>
      </div>
    </transition-group>
    <div v-if="!props.disable" class="m-t-40">
        <v-btn
          color="primary"
          variant="outlined"
          prepend-icon="mdi-plus"
          class="mt-4"
          @click="addHealthOrg"
        >
          {{ $t('ororku_umsokn_step3_add') }}
        </v-btn>
    </div>

    <!-- Other text -->
    <v-textarea
      v-model="umsokn.other"
      :readonly="props.disable"
      variant="outlined"
      density="comfortable"
      :label="t('ororku_umsokn_step9_other')"
      style="margin-top: 40px"
    />
  </div>
</template>
